/* eslint-disable no-shadow */
/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import crypto from 'crypto-js';
import moment from 'moment';
import * as queryString from 'query-string';
import { getUnregisteredPlayers } from './getUnregisteredPlayers';

import EventRegForm from '../../../../../components/EventRegForm';
import Splash from '../../../../../components/Splash';
import NcsaCollegeRecruiting from '../../../../../components/EventRegForm/NcsaCollegeRecruiting';

import { addTeammateToDivison, selfWithdrawFromEvent, getRestrictedDivisions, setRegDivisionId, addChangePartnerUpdate, resetRegisteredDoublesDivisions, lookupRegistrations } from '../../../../../actions/event-profile-actions';
import { clearCardError } from '../../../../../actions/checkout-subscription-actions';
import { showErrorPopup } from '../../../../../actions/notification-actions';
import useStartPowerCheckout from '../../../../../components/hooks/useStartPowerCheckout';
import { useSubmitGuard } from '../../../../../components/hooks/useSubmitGuard';
import { getPlayerName } from '../../../../../utils';
import { optionalEmail } from '../../../../../utils/validation';
import { discountTypeIds, eventDivisionIsPlatformPaymentOrFree, isDinglesDivision, purchaseTypeIds, isPickleBallSportTypeEvent, hasPlayerInvitationAndRegDateOpen, getRegistrationTotal, isEventType, EventTypes, areAllPlayableDivisionsAnnualPowerOnly } from '../../../../../utils/events';
import { getApiAxiosInstance } from '../../../../../utils/network';
import { nonPoweredPlayerDefaultDivisionFee, PartnerSelectionChoices, SubscriptionIntervalTypes } from '../../../../../utils/constants';
import { getUsapMessageFromError } from '../../../../../utils/usap';
import { eventFormatTypeIds, utrTypeIds, paymentProviderTypeIds, eventTypesById, sportTypeIds, regStepIds, eventStateTypeIds, registrationUserOptions, eventFlows, sidebarMessages } from '../../../../../utils/eventConstants';
import { gtag } from '../../../../../components/Analytics/gtag';
import { trackEvent } from '../../../../../components/Analytics/TrackedClick';
import { PowerFunnelGatedFeatures, PowerFunnelSurfaces, SubscriptionGateways, RallyEventRecEvents, platform } from '../../../../../components/Analytics/constants';
import { useStatsig } from '../../../../../components/hooks/useStatsig';
import * as checkoutEventRegistrationApi from '../../../../../api/checkoutEventRegistrationApi';
import {
    saveAnswers,
    validatePassword,
    validateDivisionPartner,
} from '../../../../../api/tmsEventsApi';
import DeclineInvitationPopup from '../DeclineInvitationPopup';
import RegistrationStepIndicator from '../../../../../components/RegistrationStepIndicator';

import styles from './CustomSidebarStyles.scss';
import { eventShape, routerLocationShape } from '../../../../../propTypes';

export const getTitleOverride = (event) => {
    const userRegisteredDivisions = event?.currentMember?.registeredDivisions || [];
    const hasDivisionsWithPendingInvitations = userRegisteredDivisions.some((registeredDivision) => {
        const eventDivision = event?.eventDivisions?.find(d => d.id === registeredDivision.id);
        return hasPlayerInvitationAndRegDateOpen(registeredDivision, eventDivision);
    });
    const showPreRegAlternateCopy = event?.eventStateId === eventStateTypeIds.PRE_REGISTRATION &&
        !event?.currentMember?.memberPreRegistrationInvites?.length &&
        hasDivisionsWithPendingInvitations;
    return showPreRegAlternateCopy ? 'Complete partner registration' : undefined;
};

export const fireRallyEventTelemetry = (eventName, { auth, event }) => {
    trackEvent(eventName, {
        user_id: auth?.user?.id,
        platform,
        event_id: event?.id,
    });
};

const getSubscriptionState = (user) => {
    const isMonthlySubscriber = (user?.isPowered || user?.hasPastDueSubscription) && user?.subscriptionTierType !== SubscriptionIntervalTypes.YEAR;
    const isAppleMonthlySubscriber = isMonthlySubscriber && user?.subscriptionGateway === SubscriptionGateways.APPLE;
    const isPastDueMonthly = !!user?.hasPastDueSubscription && user?.subscriptionTierType !== SubscriptionIntervalTypes.YEAR;
    const isPastDueAnnual = !!user?.hasPastDueSubscription && user?.subscriptionTierType === SubscriptionIntervalTypes.YEAR;
    const userIsAnnualPower = user?.isPowered && user?.subscriptionTierType === SubscriptionIntervalTypes.YEAR;

    const getUpsellCtaText = () => {
        if (isPastDueAnnual) { return 'RESUME POWER'; }
        if (isPastDueMonthly) { return 'UPGRADE TO POWER ANNUAL PLAN'; }
        return null;
    };

    return {
        isMonthlySubscriber,
        isAppleMonthlySubscriber,
        isPastDueAnnual,
        userIsAnnualPower,
        upsellCtaTextOverride: getUpsellCtaText(),
        upsellRedirectNoteOverride: isPastDueAnnual
            ? 'You\'ll be redirected back to complete event registration after you resume your annual subscription.'
            : null,
    };
};

function SideBarV3(props) {
    const {
        event,
        sidebarOpen,
        clubVerificationFee,
        sidebarFlow,
        handleRegPartnerAdded,
        clubId,
        setSidebarFlow,
        handleMultipleDivisionRegistrations,
        multipleDivisionRegistrations,
        handleClearMultipleDivisionRegistrations,
    } = props;

    const auth = useSelector(({ auth }) => auth);
    const parsed = queryString.parse(props.location.search);
    const { jwt } = parsed;

    const { regDivisionId } = useSelector(({ eventProfile }) => {
        const { regDivisionId } = eventProfile;
        return { regDivisionId };
    });
    const dispatch = useDispatch();
    const { startPowerCheckout } = useStartPowerCheckout();

    const { checkGate, featureGates } = useStatsig();
    const isNcsaGateEnabled = checkGate(featureGates.NCSA);
    const showPowerUpgradeStep = checkGate(featureGates.EVENT_REG_POWER_UPGRADE);

    const claimedPlayer = auth.user?.claimedPlayer;
    const areAllDivisionsAnnualPowerOnly = areAllPlayableDivisionsAnnualPowerOnly(event);
    const subscriptionState = getSubscriptionState(auth.user);
    const { userIsAnnualPower, isMonthlySubscriber, isAppleMonthlySubscriber,
        upsellCtaTextOverride, upsellRedirectNoteOverride } = subscriptionState;
    // Snapshot the user's power status at registration start so the 3-step indicator
    // doesn't flip to 2 steps when fetchAuthenticatedUser updates mid-checkout.
    const wasAnnualPowerAtStart = useRef(userIsAnnualPower);
    const showAnnualPowerStep = !wasAnnualPowerAtStart.current && areAllDivisionsAnnualPowerOnly;
    const isFlexLeagueAndTennisEvent = isEventType(event, EventTypes.FLEX_LEAGUE) && event.sportTypeId === sportTypeIds.TENNIS;
    const isCampEvent = isEventType(event, EventTypes.CAMP);
    const eventEntryFee = event?.entryFee;
    const isEventEntryFeePaid = event?.currentMember?.isEventEntryFeePaid;
    const initialRegTotal = isEventEntryFeePaid ? 0 : (eventEntryFee || 0);
    const currencySymbol = event?.currency?.symbol || '$';
    const isMultiRegistrationAllowed = event?.isMultiRegistrationAllowed;
    const { SINGLE_PLAYER, MULTIPLE_PLAYERS } = registrationUserOptions;

    const [serviceFee, setServiceFee] = useState(event?.serviceFee);
    const [requestToJoinClub, setRequestToJoinClub] = useState(true);
    const [optInForEventDeskNotifications, setOptInForEventDeskNotifications] = useState(event?.enableEventDeskNotifications);
    const [eventFee, setEventFee] = useState(0);
    const [eventFeeOffPlatform, setEventFeeOffPlatform] = useState(0);
    const [registrationTotal, setRegistrationTotal] = useState(initialRegTotal);
    const [regTotalOffPlatform, setRegTotalOffPlatform] = useState(0);
    const [regStep, setRegStep] = useState({});
    const [selectedDivisionIds, setSelectedDivisionIds] = useState([]);
    const [canceledDivisionIds, setCanceledDivisionIds] = useState([]);
    const [divisionFormatTypeFilter, setDivisionFormatTypeFilter] = useState();
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const { guardedSubmit, releaseSubmitGuard } = useSubmitGuard();
    const [switchApiResponse, setSwitchApiResponse] = useState();
    const [switchTotalRefund, setSwitchTotalRefund] = useState(0);
    const [paymentTitle, setPaymentTitle] = useState(null);
    const [showSplash, setShowSplash] = useState(false);
    const [declineDivisionData, setDeclineDivisionData] = useState(null);
    const [promoCode, setPromoCode] = useState('');
    const [promoCodeSuccess, setPromoCodeSuccess] = useState(false);
    const [promoCodeFailure, setPromoCodeFailure] = useState(false);
    const [originalEventFee, setOriginalEventFee] = useState(0);
    const [originalRegTotal, setOriginalRegTotal] = useState(initialRegTotal);
    const [invalidPromoCodePlaceHolder, setInvalidPromoCodePlaceHolder] = useState('');
    const [promoCodeApplicationText, setPromoCodeApplicationText] = useState('');
    const [totalDiscount, setTotalDiscount] = useState(0);
    const [addPartnerError, setAddPartnerError] = useState(null);
    const [registrationSubtotal, setRegistrationSubtotal] = useState(initialRegTotal);
    const [switchPlayer2, setSwitchPlayer2] = useState();
    const [registrationTypeForCamps, setRegistrationTypeForCamps] = useState(SINGLE_PLAYER);
    const [selectedNeedsPartnerPlayers, setSelectedNeedsPartnerPlayers] = useState({});
    const [lookupResults, setLookupResults] = useState({});

    const isSwitchFlow = sidebarFlow === regStepIds.DIVISIONS_SWITCH;
    const isManagementStep = regStep.id === regStepIds.DIVISIONS_ADD ||
        sidebarFlow === regStepIds.DIVISIONS_CANCEL ||
        sidebarFlow === regStepIds.DIVISIONS_SWITCH ||
        sidebarFlow === regStepIds.CHANGE_PARTNER;
    const registrationType = registrationTypeForCamps === SINGLE_PLAYER ? SINGLE_PLAYER : MULTIPLE_PLAYERS;

    const getMultiRegistrationTotal = () => {
        const divisionsFee = Object.values(multipleDivisionRegistrations).reduce((acc, { price }) => acc + price, 0);
        const serviceFee = event?.serviceFee || 0;
        const total = divisionsFee + serviceFee;
        return `${currencySymbol}${total}`;
    };

    useEffect(() => {
        if (showUpgradeStep(event)) {
            toStep(regStepIds.UPGRADE);
        } else if (event.passwordEnabled) {
            toStep(regStepIds.PASSWORD);
        } else if (isCampEvent && isMultiRegistrationAllowed) {
            toStep(regStepIds.CAMP_USER_SELECTION);
        } else {
            toStep(regStepIds.DIVISIONS);
        }
        // preselect division & calculate total
        if (regDivisionId) {
            const index = event.eventDivisions?.findIndex(div => div.id === regDivisionId);
            regToggleDivision(index);
            dispatch(setRegDivisionId());
        }
    }, []);

    useEffect(() => {
        if (sidebarFlow) {
            if (sidebarFlow === regStepIds.DIVISIONS_ADD && regStep.id !== regStepIds.DIVISIONS_ADD) {
                toStep(showUpgradeStep(event) ? regStepIds.UPGRADE : regStepIds.DIVISIONS_ADD);
            } else if (sidebarFlow === regStepIds.DIVISIONS_SWITCH && regStep.id !== regStepIds.DIVISIONS_CANCEL) {
                toStep(regStepIds.DIVISIONS_CANCEL);
            } else if (sidebarFlow === regStepIds.DIVISIONS_CANCEL && regStep.id !== regStepIds.DIVISIONS_CANCEL) {
                toStep(regStepIds.DIVISIONS_CANCEL);
            } else if (sidebarFlow === regStepIds.CHANGE_PARTNER && regStep.id !== regStepIds.CHANGE_PARTNER) {
                toStep(regStepIds.CHANGE_PARTNER);
            }
        }
    }, [sidebarFlow]);

    const partnerMemberIdKey = useMemo(
        () => selectedDivisionIds
            .map(id => event.eventDivisions?.find(d => d.id === id))
            .filter(div => div?.player2?.memberId)
            .map(div => div.player2.memberId)
            .join(','),
        [selectedDivisionIds, event.eventDivisions],
    );

    useEffect(() => {
        const partnerMemberIds = partnerMemberIdKey ? partnerMemberIdKey.split(',') : [];

        setLookupResults({});

        if (partnerMemberIds.length === 0) {
            return;
        }

        const memberIds = partnerMemberIds.map(id => parseInt(id, 10));
        const controller = new AbortController();

        const timerId = setTimeout(async () => {
            try {
                const data = await dispatch(lookupRegistrations(event.id, memberIds, controller.signal)); // NOSONAR — Redux thunk returns a promise
                const results = data.registrations.reduce((acc, r) => {
                    acc[r.memberId] = r;
                    return acc;
                }, {});
                setLookupResults(results);
            } catch {
                // On failure, conservative state persists (lookupResults stays {})
            }
        }, 250);

        return () => {
            clearTimeout(timerId);
            controller.abort();
        };
    }, [partnerMemberIdKey, event.id, dispatch]);

    const setCheckoutSubmittingFlag = (submitting) => {
        setPaymentProcessing(!!submitting);
    };

    const handleSelfWithdraw = (withdrawPayload) => {
        setPaymentProcessing(true);
        dispatch(selfWithdrawFromEvent(withdrawPayload, props.handleOverlayClick));
    };

    const toStep = (step, divisionId, changePartnerFlow, playerIsInNeedPartnerList) => {
        setAddPartnerError(null);
        switch (step) {
            case regStepIds.UPGRADE:
                setRegStep({
                    id: regStepIds.UPGRADE,
                    title: 'Upgrade to Power',
                    noPrev: true,
                });
                break;
            case regStepIds.PASSWORD: {
                setRegStep({
                    id: regStepIds.PASSWORD,
                    title: 'Enter Password',
                    noPrev: true,
                });
                break;
            }
            case regStepIds.CAMP_USER_SELECTION: {
                setRegStep({
                    id: regStepIds.CAMP_USER_SELECTION,
                    title: 'Select Player(s)',
                    noPrev: true,
                });
                break;
            }
            case regStepIds.CAMP_DIVISION_SELECTION: {
                setRegStep({
                    id: regStepIds.CAMP_DIVISION_SELECTION,
                    title: 'Select Divisions',
                });
                break;
            }
            case regStepIds.CAMP_CONFIRM: {
                setRegStep({
                    id: regStepIds.CAMP_CONFIRM,
                    title: 'Confirm Camp Selection',
                });
                break;
            }
            case regStepIds.DIVISIONS:
                setRegStep({
                    id: regStepIds.DIVISIONS,
                    title: event.currentMember?.isPlaying ? 'Update Divisons' : 'Select Divisons',
                    noPrev: true,
                });
                handleResetPromoCode();
                break;

            case regStepIds.DIVISIONS_ADD:
                setRegStep({
                    id: regStepIds.DIVISIONS_ADD,
                    title: isSwitchFlow ? 'Switch Divisions' : 'Add Divisons',
                });
                break;

            case regStepIds.DIVISIONS_CANCEL:
                setRegStep({
                    id: regStepIds.DIVISIONS_CANCEL,
                    title: isSwitchFlow ? 'Switch Divisions' : 'Withdraw',
                });
                break;

            case regStepIds.DIVISIONS_SWITCH:
                setRegStep({
                    id: regStepIds.DIVISIONS_SWITCH,
                    title: 'Switch Divisions',
                });
                break;

            case regStepIds.TEAMMATE:
                handleRegPartnerAdded(); // flag to reload event data
                setRegStep({
                    id: regStepIds.TEAMMATE,
                    title: `Add Partner`,
                    divisionId,
                    invite: 'existing',
                    errors: {
                        existingEmail: false,
                        inviteEmail: false,
                    },
                    changePartnerFlow,
                    playerIsInNeedPartnerList,
                });
                break;

            case regStepIds.CHANGE_PARTNER:
                setRegStep({
                    id: regStepIds.CHANGE_PARTNER,
                    title: 'Add/Change Partner',
                });
                break;

            case regStepIds.QUESTIONS:
                setRegStep({
                    id: regStepIds.QUESTIONS,
                    title: 'Event Questions',
                });
                break;

            case regStepIds.CAMP_QUESTIONS:
                setRegStep({
                    id: regStepIds.CAMP_QUESTIONS,
                    title: 'Event Questions',
                });
                break;

            case regStepIds.CONFIRM:
                setRegStep({
                    id: regStepIds.CONFIRM,
                    title: 'Confirm Selection',
                });
                break;
            case regStepIds.PAYMENT:
                setRegStep({
                    id: regStepIds.PAYMENT,
                    title: 'Enter Payment',
                });
                break;
            case regStepIds.PAYMENT_WAIT:
                setRegStep({
                    id: regStepIds.PAYMENT_WAIT,
                    title: 'Processing Payment',
                });
                break;
            case regStepIds.NCSA_PROMO:
                setRegStep({
                    id: regStepIds.NCSA_PROMO,
                    title: 'Confirmation',
                    noPrev: true,
                });
                break;
            case regStepIds.NCSA_REG:
                setRegStep({
                    id: regStepIds.NCSA_REG,
                    title: 'NCSA College Recruiting',
                    noPrev: true,
                });
                break;
            case regStepIds.NCSA_FINISH:
                setRegStep({
                    id: regStepIds.NCSA_FINISH,
                    title: 'NCSA College Recruiting',
                    noPrev: true,
                });
                break;
            default:
                break;
        }
    };

    const toggleDivision = (id) => {
        setSelectedDivisionIds(prevDivisionIds => {
            let selectedDivisionIdsUpdate = [...prevDivisionIds];
            const idx = selectedDivisionIdsUpdate.indexOf(id);
            if (idx > -1) {
                selectedDivisionIdsUpdate.splice(idx, 1);
            } else if (isSwitchFlow) {
                // radio button behavior
                selectedDivisionIdsUpdate = [id];
            } else {
                // multiselect behavior
                selectedDivisionIdsUpdate.push(id);
            }
            return selectedDivisionIdsUpdate;
        });
    };

    const getPromoCodeApplicationText = (discountType, discountFactor) => {
        switch (discountType) {
            case discountTypeIds.PERCENTAGE:
                return `${discountFactor}% off your first order`;
            case discountTypeIds.FLAT_AMMOUNT:
                return `${currencySymbol}${discountFactor} off your first order`;
            default:
                return null;
        }
    };

    // the goal of this function is to apply the promo code so it reflects on the UI and to let the entire component know that a coupon was applied so when stripePopulatePaymentRequestBody gets called, it knows to grab more info
    const handlePromoCodeApplication = async () => {
        const { eventTypeId, id } = event;
        const lineItems = selectedDivisionIds.map(divisionId => ({
            eventId: id,
            divisionId,
            eventTypeId,
        }));
        const promoCodePostBody = {
            purchaseTypeId: purchaseTypeIds.EVENT_REGISTRATION,
            promoCode,
            lineItems,
        };
        // api call with postbody to validate -> if successful-> lock field and apply discount on UI
        try {
            const apiInstance = getApiAxiosInstance(jwt);
            const { data } = await checkoutEventRegistrationApi.validatePromoCode(apiInstance, promoCodePostBody);
            const { discountTypeId, discountFactor, subtotal, discountedSubtotal, discount } = data;
            const total = isEventEntryFeePaid ? discountedSubtotal : discountedSubtotal + eventEntryFee;

            setRegistrationTotal(total);
            setEventFee(subtotal);
            setTotalDiscount(parseFloat(discount?.toFixed(2) || '0'));
            setPromoCodeSuccess(true);
            setPromoCodeFailure(false);
            setPromoCodeApplicationText(getPromoCodeApplicationText(discountTypeId, discountFactor));
        } catch (e) {
            setPromoCodeFailure(true);
            setInvalidPromoCodePlaceHolder(promoCode);
            setPromoCodeApplicationText(e.response.data.DisplayMessage);
        }
    };

    const handleResetPromoCode = () => {
        setEventFee(originalEventFee);
        setRegistrationTotal(originalRegTotal);
        setPromoCode('');
        setPromoCodeSuccess(false);
        setPromoCodeFailure(false);
    };

    const handlePromoCodeRemoval = () => {
        handleResetPromoCode();
    };

    const handlePromoCodeChange = e => {
        setPromoCode(e.target.value);
    };

    const regToggleDivision = (i, indexArray = []) => {
        // method updated to allow multiple division being toggled at the same time, to because total calculation is happening here
        const { workflowVersion, paymentProviderTypeId } = event;
        let eventFee = 0;
        let eventFeeOffPlatform = 0;
        let total = 0;
        const isToggledIndex = (j) => i === j || indexArray.includes(j);
        const shouldToggleDivision = (div, j) => {
            if (isSwitchFlow) {
                return isToggledIndex(j);
            }
            const isSelected = selectedDivisionIds.includes(div.id);
            const isTogglingOn = isToggledIndex(j) && !isSelected;
            const isStaying = !isToggledIndex(j) && isSelected;
            return isTogglingOn || isStaying;
        };
        event.eventDivisions.forEach((div, j) => {
            if (shouldToggleDivision(div, j)) {
                const eventDivisionIsPlatformPaymentOrFreeResult = eventDivisionIsPlatformPaymentOrFree(div);
                const addToEventFee = (eventDivisionIsPlatformPaymentOrFreeResult && !event.allowOffPlatformPayments);
                const isDivisionDingles = isDinglesDivision(div);

                if (!auth.user.isPowered &&
                    event.utrTypeId === utrTypeIds.SANCTIONED &&
                    workflowVersion > 1 &&
                    clubVerificationFee > 0 &&
                    addToEventFee &&
                    !isDivisionDingles) {
                    eventFee += (div.entryFee + clubVerificationFee);
                } else if (addToEventFee) {
                    eventFee += div.entryFee;
                } else {
                    eventFeeOffPlatform += div.entryFee;
                }
            }
            if (i === j || indexArray.includes(j)) {
                toggleDivision(div.id);
            }
        });
        if (eventFee > 0) {
            if (paymentProviderTypeId === paymentProviderTypeIds.STRIPE) {
                total = eventFee;
            }
        }

        total += initialRegTotal;
        setEventFee(eventFee);
        setEventFeeOffPlatform(eventFeeOffPlatform);
        setRegistrationTotal(total);
        setRegistrationSubtotal(total);
        setRegTotalOffPlatform(eventFeeOffPlatform);
        // keep in state in case if promotion was removed to easily revert without having to map through all of the selected divisions
        setOriginalEventFee(eventFee);
        setOriginalRegTotal(total);
    };

    const regIsTabInvalid = (step) => {
        if (event && step === regStepIds.DIVISIONS_ADD) {
            let selectedDivs = 0;
            const missingPlayer = event.eventDivisions.some((div) => {
                if (selectedDivisionIds.includes(div.id)) {
                    selectedDivs += 1;
                }
                return selectedDivisionIds.includes(div.id) && div.divisionFormatTypeId !== eventFormatTypeIds.SINGLES && !div.player2;
            });

            return selectedDivs === 0 || missingPlayer;
        }
        return false;
    };

    const regSelectTeammate = (selectedPlayer) => {
        if (selectedPlayer.id) {
            setRegStep({ ...regStep, selectedPlayer });
        } else if (selectedPlayer.query) {
            setRegStep({
                ...regStep,
                invite: 'invite',
                selectedPlayer: null,
                inviteName: selectedPlayer.query,
            });
        }
    };

    const regAddTeammate = (e) => {
        setRegStep({ ...regStep, invite: e.target.value });
    };

    const regAddTeammateToDivison = () => {
        let player2 = {};
        switch (regStep.invite) {
            case PartnerSelectionChoices.EXISTING:
            case PartnerSelectionChoices.ADD_NEEDS_PARTNER:
                player2 = {
                    ...regStep.selectedPlayer,
                    needsPartner: false,
                };
                // filter out already selected players from need partner list
                setSelectedNeedsPartnerPlayers({
                    ...selectedNeedsPartnerPlayers,
                    [regStep.div]: `${regStep.selectedPlayer.id}-${regStep.selectedPlayer.divisionId}`,
                });
                break;
            case PartnerSelectionChoices.INVITE: {
                const nameArray = regStep.inviteName?.split(' ', 2);
                player2 = {
                    name: regStep.inviteName,
                    firstName: nameArray[0] || '',
                    lastName: nameArray[1] || '',
                    email: regStep.inviteEmail,
                    needsPartner: false,
                };
                setSelectedNeedsPartnerPlayers({
                    ...selectedNeedsPartnerPlayers,
                    [regStep.div]: null,
                });
                break;
            }
            case PartnerSelectionChoices.NEEDS_PARTNER:
                player2 = {
                    name: 'Need Partner',
                    firstName: 'Need',
                    lastName: 'Partner',
                    needsPartner: true,
                };
                setSelectedNeedsPartnerPlayers({
                    ...selectedNeedsPartnerPlayers,
                    [regStep.div]: null,
                });
                break;
            default:
                break;
        }
        if (regStep.changePartnerFlow) {
            dispatch(addChangePartnerUpdate({ divisionId: regStep.divisionId, player2 }));
            toStep(regStepIds.CHANGE_PARTNER);
        } else {
            dispatch(addTeammateToDivison({ divisionId: regStep.divisionId, player2 }));
            toStep(regStepIds.DIVISIONS_ADD);
        }
    };

    const validateEmails = async () => {
        const errors = {};

        switch (regStep.invite) {
            case PartnerSelectionChoices.EXISTING:
                errors.existingEmail = optionalEmail(regStep.selectedPlayer.email);
                break;
            case PartnerSelectionChoices.INVITE:
                errors.inviteEmail = optionalEmail(regStep.inviteEmail);
                break;
            case PartnerSelectionChoices.NEEDS_PARTNER:
            default:
                break;
        }

        if (errors.existingEmail || errors.inviteEmail) {
            setRegStep({
                ...regStep,
                errors,
            });
            return;
        }

        const needsPartnerValidation = regStep.invite === PartnerSelectionChoices.EXISTING && regStep?.selectedPlayer?.memberId;
        if (!needsPartnerValidation) {
            regAddTeammateToDivison();
            return;
        }

        if (regStep.selectedPlayer.memberId === auth.user.id) {
            setAddPartnerError(`You may not select yourself as a partner.`);
            return;
        }

        const currentDivision = event.eventDivisions.find(div => div.id === regStep.divisionId);
        try {
            await validateDivisionPartner(getApiAxiosInstance(jwt), event.id, currentDivision.id, regStep.selectedPlayer.memberId);
            regAddTeammateToDivison();
        } catch (e) {
            setAddPartnerError(e?.response?.data?.Message);
        }
    };

    const regAddUnclaimedEmail = (e) => {
        const selectedPlayer = { ...regStep.selectedPlayer, email: e.target.value };
        setRegStep({ ...regStep, selectedPlayer });
    };

    const regHandleInviteChange = (e) => {
        setRegStep({
            ...regStep,
            [e.target.name]: e.target.value,
        });
    };

    const regHandleSubmitQuestions = async ({ values }) => {
        const isInCampFlow = sidebarFlow === eventFlows.CAMP_FLOW;

        setPaymentProcessing(true);
        const ids = {};
        Object.keys(values).forEach(id => {
            ids[id] = true;
        });

        const list = {};
        Object.entries(values).forEach((entry) => {
            const array = entry[0].split('-');
            if (!list[`${array[1]}`]) {
                list[`${array[1]}`] = [];
            }
            list[`${array[1]}`].push({
                questionId: array[2],
                answer: entry[1],
            });
        });

        const selectedDivisions = event.eventDivisions.filter(d => selectedDivisionIds.includes(d.id));

        selectedDivisions.forEach((div) => {
            if (div.player2) {
                if (div.player2.id) {
                    if (list[`${div.player2.id}`]) {
                        div.player2.partnerAnswers = [{
                            partnerId: div.player2.id,
                            answers: list[`${div.player2.id}`],
                            email: div.player2.email ? div.player2.email : null,
                        }];
                    }
                } else if (list[`${crypto.MD5(div.player2.email).toString()}`]) {
                    div.player2.partnerAnswers = [{
                        email: div.player2.email,
                        answers: list[`${crypto.MD5(div.player2.email).toString()}`],
                    }];
                }
            }
        });

        const userAnswers = list[`${auth.user.claimedPlayer.playerId}`];
        if (userAnswers) {
            try {
                await saveAnswers(getApiAxiosInstance(jwt), event.id, userAnswers);
                trackEvent('Questions Answered', { memberId: auth?.user?.id, eventId: event?.id, registrationType });
                setPaymentProcessing(false);
                if (isInCampFlow) {
                    toStep(regStepIds.CAMP_CONFIRM);
                } else {
                    toStep(regStepIds.CONFIRM);
                }
            } catch (e) {
                dispatch(showErrorPopup({
                    message: 'Error saving questions',
                    error: e,
                }));
                setPaymentProcessing(false);
            }
        } else {
            setPaymentProcessing(false);
            if (isInCampFlow) {
                toStep(regStepIds.CAMP_CONFIRM);
            } else {
                toStep(regStepIds.CONFIRM);
            }
        }
    };

    const regHandleSubmitPassword = () => {
        if (isCampEvent && isMultiRegistrationAllowed) {
            toStep(regStepIds.CAMP_USER_SELECTION);
        } else {
            toStep(regStepIds.DIVISIONS);
        }
    };

    const regHandleConfirm = () => {
        const { updatedTotal: total, updatedServiceFee } =
            getRegistrationTotal({ total: registrationTotal, serviceFee });

        setServiceFee(updatedServiceFee);

        if (total > 0 && !event?.allowOffPlatformPayments) {
            toStep(regStepIds.PAYMENT);
        } else if (!guardedSubmit(() => {
            setPaymentProcessing(true);
            submitFreeEventRegistration();
        })) {
            return;
        }
        gtag('event', {
            category: 'Checkout Started',
            context: `eventId: ${event.id}`,
        });

        trackEvent('Selections Confirmed', {
            memberId: auth?.user?.id,
            eventId: event?.id,
            registrationType: registrationUserOptions.SINGLE_PLAYER,
        });

        if (parsed.rally) {
            fireRallyEventTelemetry(RallyEventRecEvents.RALLY_EVENT_CHECKOUT_STARTED, { auth, event });
        }
    };

    const regHandleValidatePassword = async (values) => {
        try {
            await validatePassword(getApiAxiosInstance(jwt), event.id, values);
            return true;
        } catch (e) {
            console.error('Password validation failed');
            return false;
        }
    };

    // WV3: called after API accepts the checkout request
    const onCheckoutProcessStarted = () => {
        toStep(regStepIds.PAYMENT_WAIT);
    };

    // WV2: called after API and legacy checkout form have both talked to Stripe
    const onPaymentProcessStarted = (res) => {
        // this is a paymentMethodId not a Payments API CheckoutId
        postRegistration(res.checkoutId);
        setPaymentProcessing(false);
        releaseSubmitGuard();
        postRegCheck(res);
    };

    const regFinishPayment = () => {
        postRegCheck();
    };

    const regRequestToJoinClub = () => {
        setRequestToJoinClub(!requestToJoinClub);
    };

    const regRequestToOptInForEventDeskNotifications = () => {
        setOptInForEventDeskNotifications(!optInForEventDeskNotifications);
    };

    // check if we have any extra sidebar steps after completing the reg flow
    const postRegCheck = (regResponse) => {
        const isPickleballEvent = isPickleBallSportTypeEvent(event?.sportTypeId);
        const ncsaCount = parseInt(localStorage.getItem(`ncsa-${auth.user.id}`) || 0);
        let currentAge = 0;
        if (auth.user.birthdate) {
            currentAge = moment().diff(auth.user.birthdate, 'years');
        }
        // show NCSA promo to users between 13-18, max 3 times
        if (isNcsaGateEnabled && currentAge >= 13 && currentAge <= 18 && ncsaCount < 3 && !auth.user.isRegisteredWithNcsa && !isPickleballEvent) {
            localStorage.setItem(`ncsa-${auth.user.id}`, ncsaCount + 1);
            // segment track call
            const path = '/events/{eventId}';
            const properties = {
                path,
                url: path,
                title: 'Event Registration NCSA Popup',
            };
            gtag('view', {
                category: 'NCSA',
                context: 'Show event registration ncsa promo',
            });
            // mapped to GA4
            if (window?.analytics) {
                window.analytics.page('Show event registration ncsa promo', 'Event Registration', properties);
            }
            toStep(regStepIds.NCSA_PROMO);
        } else if (sidebarFlow === regStepIds.DIVISIONS_SWITCH) {
            toStep(regStepIds.DIVISIONS_SWITCH);
        } else if (sidebarFlow === eventFlows.CAMP_FLOW) {
            props.toMultiRegConfirmation();
        } else {
            props.toRegConfirmation(regResponse);
        }
    };

    // STRIPE LOGIC

    const mapSelectedDivisionsForSignup = (isSwitchFlow) => {
        const selectedDivisions = event.eventDivisions.filter(d => selectedDivisionIds.includes(d.id));
        return selectedDivisions.map((div) => {
            const division = {
                divisionId: div.id,
            };
            if (isSwitchFlow) {
                division.eventId = event.id;
            }
            const player2 = isSwitchFlow ? switchPlayer2 : div.player2;
            if (player2) {
                if (player2.needsPartner) {
                    division.needsPartner = true;
                } else if (player2.divisionId) {
                    // picked player from need partner list - can be picked from other divisions
                    division.movePartnerFromDivision = {
                        fromDivisionId: player2.divisionId,
                        memberId: player2.id,
                    };
                } else if (player2.id) {
                    // picked from player search
                    if (player2.profileId > 0) {
                        division.partnerIds = [player2.id];
                    } else {
                        division.playerInvites = [
                            {
                                id: player2.id,
                                name: player2.displayName,
                                email: player2.email,
                            },
                        ];
                    }
                } else {
                    division.playerInvites = [
                        {
                            name: player2.name,
                            email: player2.email,
                        },
                    ];
                }
                if (player2.partnerAnswers) {
                    division.partnerAnswers = player2.partnerAnswers;
                }
            }
            return division;
        });
    };

    const stripePopulatePaymentRequestBody = () => {
        const postData = {
            requestToJoinClub,
        };
        postData.divisions = mapSelectedDivisionsForSignup();
        if (promoCodeSuccess) {
            postData.promoCode = promoCode;
        }

        if (event?.enableEventDeskNotifications) {
            postData.smsOptIn = optInForEventDeskNotifications;
        }

        return postData;
    };

    const stripePopulateSwitchPaymentRequestBody = () => {
        const postData = {
            oldDivisionIds: canceledDivisionIds,
        };
        postData.newDivisions = mapSelectedDivisionsForSignup(true);
        return postData;
    };

    const submitFreeEventRegistration = async () => {
        try {
            const postData = stripePopulatePaymentRequestBody();
            const res = await checkoutEventRegistrationApi.legacyMemberRegistrationCheckout(getApiAxiosInstance(jwt), event.id, postData);
            onPaymentProcessStarted(res.data);
        } catch (error) {
            const retMessage = (error?.response?.data?.message?.indexOf('suspended') > -1) ? error.response.data.message : 'Error completing registration';
            const description = error?.response?.data?.DisplayMessage || error?.response?.data?.message;
            const usapValidationMessage = getUsapMessageFromError(error);
            setPaymentProcessing(false);
            releaseSubmitGuard();

            trackEvent(`submitFreeEventRegistration-${retMessage}`, {
                page: 'Sidebar',
                action: `Error-${usapValidationMessage}`,
                memberId: auth?.user?.id,
                eventId: event?.id,
                client: 'Web',
                timestamp: moment.utc(),
            });

            dispatch(showErrorPopup({
                message: retMessage,
                error: description || usapValidationMessage || 'Service is unreachable',
            }));
        }
    };

    // STRIPE LOGIC END

    const handleUpgradeToPowerClick = () => {
        if (!areAllDivisionsAnnualPowerOnly) {
            // Non-annual-power-only events: close sidebar and show popup
            props.handleOverlayClick();
            startPowerCheckout({
                surface: PowerFunnelSurfaces.EVENT_PROFILE_UPGRADE,
                gatedFeature: PowerFunnelGatedFeatures.EVENT_REGISTRATION,
            });
        }
        // Annual-power-only events: sub-step transition handled inside EventRegForm
    };

    const showUpgradeStep = (event) => {
        const userIsPowered = auth.user?.isPowered;
        const eventVerificationFeeParsed = parseFloat(event.verificationFee) || 0;
        const clubVerificationFee = eventVerificationFeeParsed >= 0 ? eventVerificationFeeParsed : nonPoweredPlayerDefaultDivisionFee;
        const isSanctioned = event.utrType?.value === 'sanctioned' || event.utrTypeId === utrTypeIds.SANCTIONED;
        const checkForSanctioned = !userIsPowered && isSanctioned && event?.workflowVersion > 1 && clubVerificationFee > 0;
        return ((checkForSanctioned || showAnnualPowerStep) && showPowerUpgradeStep);
    };

    const handleOverlayClick = () => {
        if (paymentProcessing) {
            return;
        }
        if (regStep.id === regStepIds.NCSA_PROMO || regStep.id === regStepIds.NCSA_REG || regStep.id === regStepIds.NCSA_FINISH) {
            handleNcsaCta();
        } else {
            let regStepUpdate = null;
            dispatch(addChangePartnerUpdate({}));
            dispatch(resetRegisteredDoublesDivisions({}));
            dispatch(clearCardError());
            props.handleOverlayClick();
            if (showUpgradeStep(event)) {
                regStepUpdate = {
                    id: regStepIds.UPGRADE,
                    title: 'Upgrade to Power',
                };
            } else if (event.passwordEnabled) {
                regStepUpdate = {
                    id: regStepIds.PASSWORD,
                    title: 'Enter Password',
                };
            } else {
                regStepUpdate = {
                    id: regStepIds.DIVISIONS,
                    title: event.currentMember?.isPlaying ? 'Update Divisions' : 'Select Divisions',
                };
            }
            setRegStep(regStepUpdate);
        }
    };

    const getStepAfterUpgrade = () => {
        if (sidebarFlow === regStepIds.DIVISIONS_ADD) {
            return regStepIds.DIVISIONS_ADD;
        }
        if (event.passwordEnabled) {
            return regStepIds.PASSWORD;
        }
        if (isCampEvent && isMultiRegistrationAllowed) {
            return regStepIds.CAMP_USER_SELECTION;
        }
        return regStepIds.DIVISIONS;
    };

    const handleDeclineUpgradeToPowerClick = () => {
        toStep(getStepAfterUpgrade());
        props.handleDeclineUpgradeToPowerClick();
    };

    const postRegistration = (paymentIntentId) => {
        const { name, eventTypeId } = event;
        let isPaidHit = false;
        let isFlexLeagueEvent = false;
        let category;
        let path = registrationTotal > 0 ? '/event/registered-paid' : '/event/registered-free';

        switch (eventTypeId) {
            case EventTypes.FLEX_LEAGUE:
                category = 'flex league';
                path = `${path}/flex-league`;
                isFlexLeagueEvent = true;
                break;
            case EventTypes.TEAM_LEAGUE:
                category = 'team tennis';
                path = `${path}/team-tennis`;
                break;
            case EventTypes.TEAM_MATCH:
                category = 'adult league';
                path = `${path}/adult-league`;
                break;
            case EventTypes.PAID_HIT:
                category = 'paid hit';
                path = `${path}/paidhit`;
                isPaidHit = true;
                break;
            default:
                category = registrationTotal > 0 ? 'paid event' : 'free event';
                break;
        }

        const properties = {
            path,
            url: path,
            title: name,
        };
        gtag('view', {
            category,
            context: `event registration - ${JSON.stringify(properties)}`,
        });
        gtag('event', {
            category: 'Order Completed',
            context: `eventId: ${event.id}`,
        });
        if (window?.analytics) {
            window.analytics.page(category, 'event registration', properties);
            window.analytics.track('Order Completed', {
                userId: auth.user.id,
                eventId: event.id,
                revenue: serviceFee ? registrationTotal + serviceFee : registrationTotal,
                serviceFee,
                registrationType,
            });
        }
        if (parsed.rally) {
            fireRallyEventTelemetry(RallyEventRecEvents.RALLY_EVENT_REGISTRATION_COMPLETED, { auth, event });
        }
        if (registrationTotal > 0) {
            let context = `Event: ${eventTypesById[eventTypeId]}`;
            if (event?.eventTours?.length) {
                context += ` - Tour: ${event?.eventTours[0].name}`;
            }
            gtag('purchase', {
                category: 'Event Registration',
                context,
                value: registrationTotal,
                transaction_id: paymentIntentId,
            });
        }
        if (isFlexLeagueEvent && registrationTotal > 0) {
            // commenting out for now in case we will return back to the facebook pixel for purchase tracking
            // if (window && window.fbq) {
            //     window.fbq('track', 'Purchase', { value: registrationTotal, currency: 'USD', content_name: path });
            // }
            if (window?.pixie) {
                window.pixie('event', 'Flexleagues');
            }
        }
        if (isPaidHit && registrationTotal > 0) {
            // commenting out for now in case we will return back to the facebook pixel for purchase tracking
            // if (window && window.fbq) {
            //     window.fbq('track', 'Purchase', { value: registrationTotal, currency: 'USD', content_name: path });
            // }
            if (window?.pixie) {
                window.pixie('event', 'Paidhits');
            }
        }
    };

    const toggleSplashBallLoader = () => {
        setShowSplash(showSplash => (!showSplash));
    };

    const declinePartnerInvitation = (data) => {
        setDeclineDivisionData(data);
    };

    const closeDeclinePartnerInvitationPopup = () => {
        setDeclineDivisionData(null);
    };

    const handleBackClick = () => {
        if (paymentProcessing) {
            return;
        }
        const returnToCampMemberSelection = (isCampEvent && (regStep.id === regStepIds.DIVISIONS) && isMultiRegistrationAllowed);
        if (sidebarFlow === regStepIds.DIVISIONS_SWITCH) {
            if (regStep.id === regStepIds.DIVISIONS_ADD) {
                setSelectedDivisionIds([]);
                toStep(regStepIds.DIVISIONS_CANCEL);
            } else if (regStep.id === regStepIds.DIVISIONS_SWITCH || regStep.id === regStepIds.TEAMMATE) {
                toStep(regStepIds.DIVISIONS_ADD);
            } else if (regStep.id === regStepIds.PAYMENT) {
                toStep(regStepIds.DIVISIONS_SWITCH);
            }
        } else if (sidebarFlow === eventFlows.CAMP_FLOW || returnToCampMemberSelection) {
            const isRegStepOnADivisionSelection = regStep.id === regStepIds.CAMP_DIVISION_SELECTION || regStep.id === regStepIds.DIVISIONS;
            if (isRegStepOnADivisionSelection) {
                toStep(regStepIds.CAMP_USER_SELECTION);
            } else if (regStepIds.CAMP_CONFIRM) {
                toStep(regStepIds.CAMP_DIVISION_SELECTION);
            } else if (regStepIds.QUESTIONS) {
                toStep(regStepIds.CAMP_DIVISION_SELECTION);
            }
        } else if (sidebarFlow === regStepIds.CHANGE_PARTNER) {
            toStep(regStepIds.CHANGE_PARTNER);
        } else {
            toStep(regStepIds.DIVISIONS);
        }
    };

    const shouldShowBackArrow = () => {
        const returnToCampMemberSelection = (isCampEvent && (regStep.id === regStepIds.DIVISIONS) && isMultiRegistrationAllowed);

        // why would switch divisions ignore noPrev?
        if (sidebarFlow === regStepIds.DIVISIONS_SWITCH) {
            return regStep.id !== regStepIds.DIVISIONS_CANCEL &&
                !(regStep.id === regStepIds.DIVISIONS_SWITCH && switchApiResponse) &&
                regStep.id !== regStepIds.PAYMENT_WAIT;
        }
        if (returnToCampMemberSelection) {
            return true;
        }

        return !regStep.noPrev &&
            regStep.id !== regStepIds.DIVISIONS &&
            regStep.id !== regStepIds.DIVISIONS_ADD &&
            regStep.id !== regStepIds.DIVISIONS_CANCEL &&
            regStep.id !== regStepIds.CHANGE_PARTNER &&
            regStep.id !== regStepIds.PAYMENT_WAIT;
    };

    const showBackArrow = shouldShowBackArrow();

    const handleSetCanceledDivisionIds = (divisionIds) => {
        setCanceledDivisionIds(divisionIds);
    };

    const handleSetSwitchApiResponse = (divisionIds) => {
        setSwitchApiResponse(divisionIds);
    };

    const handleSetSwitchTotalRefund = (total) => {
        setSwitchTotalRefund(total);
    };

    const handleSetDivisionFormatTypeFilter = (formatType) => {
        setDivisionFormatTypeFilter(formatType);
    };

    const handleSetSwitchPlayer2 = (player2) => {
        setSwitchPlayer2(player2);
    };

    const handleNcsaCta = () => {
        if (sidebarFlow === eventFlows.CAMP_FLOW) {
            props.toMultiRegConfirmation();
        } else {
            props.toRegConfirmation(true);
        }
    };

    const titleOverride = getTitleOverride(event);

    return (
        <>
            <div className={styles.sidebarWrapper}>
                    <nav className={`${styles.navbarCustomStyles} navbar navbar-light purple-grad `} data-testid="event-profile.sidebar.navbar">
                        <div className="container" style={{ position: 'relative' }}>
                            <div className="col d-flex justify-content-center align-items-center">
                                {showBackArrow && (
                                    <div
                                        className="back-area"
                                        onClick={handleBackClick}
                                        title={paymentProcessing ? sidebarMessages.PAYMENT_PROCESSING : undefined}
                                    >
                                        <i className="utr utr-prev" />
                                    </div>
                                )}
                                {areAllDivisionsAnnualPowerOnly && !isManagementStep ? (
                                    <>
                                        <RegistrationStepIndicator
                                            showAnnualPowerStep={showAnnualPowerStep}
                                            isUpgradeStep={regStep.id === regStepIds.UPGRADE}
                                            isCheckoutStep={regStep.id === regStepIds.PAYMENT || regStep.id === regStepIds.PAYMENT_WAIT}
                                            hideSteps={isAppleMonthlySubscriber}
                                            hideTitle={userIsAnnualPower}
                                        />
                                        <div className="close-area" style={{ position: 'absolute', top: 0, right: 0, cursor: 'pointer', zIndex: 2, padding: 8 }} onClick={handleOverlayClick} data-testid="event-profile.sidebar.annual-power-close-btn">
                                            <img className="close-icon" style={{ width: 16, height: 16 }} src="/img/icons/sidebar-close-icon.svg" data-testid="event-profile.sidebar.annual-power-close-icon" />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div>
                                            <div className={`${styles.customTitle} text-highlight2 text-center`}>
                                                {titleOverride || paymentTitle || regStep.title}
                                            </div>
                                            <div className={`${styles.customSubTitle} text-center text-small`}>
                                                {claimedPlayer ? getPlayerName(claimedPlayer.firstName, claimedPlayer.lastName) : ''}
                                            </div>
                                            {!!regStep.subTitle && (
                                                <div className="text-center text-small">
                                                    {regStep.subTitle}
                                                </div>
                                            )}
                                        </div>
                                        <div className="close-area" onClick={handleOverlayClick} title={paymentProcessing ? sidebarMessages.PAYMENT_PROCESSING : undefined} data-testid="event-profile.sidebar.close-btn">
                                            <img className="close-icon" src="/img/icons/sidebar-close-icon.svg" data-testid="event-profile.sidebar.close-icon" />
                                        </div>
                                    </>
                                )}
                            </div>

                        </div>
                    </nav>
                    <Splash showSidebarLoader={showSplash} />
                    <div className={`${styles.customContainerStyles} content`}>
                        <EventRegForm
                            step={regStep}
                            event={event}
                            eventFee={eventFee}
                            eventFeeOffPlatform={eventFeeOffPlatform}
                            eventEntryFee={eventEntryFee}
                            isEventEntryFeePaid={isEventEntryFeePaid}
                            total={registrationTotal}
                            subtotal={registrationSubtotal}
                            totalOffPlatform={regTotalOffPlatform}
                            toStep={toStep}
                            toggleDivision={regToggleDivision}
                            isTabInvalid={regIsTabInvalid}
                            selectTeammate={regSelectTeammate}
                            addTeammate={regAddTeammate}
                            addTeammateToDivision={validateEmails}
                            addUnclaimedEmail={regAddUnclaimedEmail}
                            handleInviteChange={regHandleInviteChange}
                            handleSubmitQuestions={regHandleSubmitQuestions}
                            handleConfirm={regHandleConfirm}
                            handleSubmitPassword={regHandleSubmitPassword}
                            handleValidatePassword={regHandleValidatePassword}
                            onCheckoutProcessStarted={onCheckoutProcessStarted}
                            finishPayment={regFinishPayment}
                            submitting={paymentProcessing}
                            user={auth.user}
                            unRegisteredPlayers={getUnregisteredPlayers(selectedDivisionIds, event, auth.user?.claimedPlayer, lookupResults)}
                            requestToJoinClub={requestToJoinClub}
                            handleRequestToJoinClubChange={regRequestToJoinClub}
                            optInForEventDeskNotifications={optInForEventDeskNotifications}
                            handleOptInForEventDeskNotificationsChange={regRequestToOptInForEventDeskNotifications}
                            isMonthlySubscriber={isMonthlySubscriber}
                            isAppleMonthlySubscriber={isAppleMonthlySubscriber}
                            upsellCtaTextOverride={upsellCtaTextOverride}
                            upsellRedirectNoteOverride={upsellRedirectNoteOverride}
                            handleUpgradeToPowerClick={handleUpgradeToPowerClick}
                            handleDeclineUpgradeToPowerClick={handleDeclineUpgradeToPowerClick}
                            onUpgradeComplete={() => toStep(getStepAfterUpgrade())}
                            setCheckoutSubmittingFlag={setCheckoutSubmittingFlag}
                            stripePopulatePaymentRequestBody={stripePopulatePaymentRequestBody}
                            stripePopulateSwitchPaymentRequestBody={stripePopulateSwitchPaymentRequestBody}
                            onPaymentProcessStarted={onPaymentProcessStarted}
                            selectedDivisionIds={selectedDivisionIds}
                            submitSelfWithdraw={handleSelfWithdraw}
                            sidebarOpen={sidebarOpen}
                            getRestrictedDivisions={(args) => dispatch(getRestrictedDivisions(args))}
                            setPaymentTitle={setPaymentTitle}
                            toggleSplashBallLoader={toggleSplashBallLoader}
                            declinePartnerInvitation={declinePartnerInvitation}
                            handlePromoCodeApplication={handlePromoCodeApplication}
                            isFlexLeagueAndTennisEvent={isFlexLeagueAndTennisEvent}
                            promoCodeSuccess={promoCodeSuccess}
                            promoCodeFailure={promoCodeFailure}
                            handlePromoCodeRemoval={handlePromoCodeRemoval}
                            promoCode={promoCode}
                            handlePromoCodeChange={handlePromoCodeChange}
                            invalidPromoCodePlaceHolder={invalidPromoCodePlaceHolder}
                            promoCodeApplicationText={promoCodeApplicationText}
                            totalDiscount={totalDiscount}
                            serviceFee={serviceFee}
                            addPartnerError={addPartnerError}
                            sidebarFlow={sidebarFlow}
                            handleCloseClick={() => props.handleOverlayClick()}
                            canceledDivisionIds={canceledDivisionIds}
                            setCanceledDivisionIds={handleSetCanceledDivisionIds}
                            switchApiResponse={switchApiResponse}
                            setSwitchApiResponse={handleSetSwitchApiResponse}
                            switchTotalRefund={switchTotalRefund}
                            setSwitchTotalRefund={handleSetSwitchTotalRefund}
                            divisionFormatTypeFilter={divisionFormatTypeFilter}
                            setDivisionFormatTypeFilter={handleSetDivisionFormatTypeFilter}
                            switchPlayer2={switchPlayer2}
                            setSwitchPlayer2={handleSetSwitchPlayer2}
                            showPreRegAlternateCopy={!!titleOverride}
                            clubId={clubId}
                            setRegistrationTypeForCamps={setRegistrationTypeForCamps}
                            registrationTypeForCamps={registrationTypeForCamps}
                            setSidebarFlow={setSidebarFlow}
                            handleMultipleDivisionRegistrations={handleMultipleDivisionRegistrations}
                            multipleDivisionRegistrations={multipleDivisionRegistrations}
                            selectedNeedsPartnerPlayers={selectedNeedsPartnerPlayers}
                            setSelectedDivisionIds={setSelectedDivisionIds}
                            getMultiRegistrationTotal={getMultiRegistrationTotal}
                            handleClearMultipleDivisionRegistrations={handleClearMultipleDivisionRegistrations}
                            location={props.location}
                        />
                        <NcsaCollegeRecruiting
                            step={regStep}
                            toStep={toStep}
                            onSkipClick={handleOverlayClick}
                            toRegConfirmation={handleNcsaCta}
                            user={auth.user}
                        />
                    </div>
            </div>
            {declineDivisionData && (
                <DeclineInvitationPopup
                    event={event}
                    declineDivisionData={declineDivisionData}
                    closePopup={closeDeclinePartnerInvitationPopup}
                    toggleSplashBallLoader={toggleSplashBallLoader}
                    isSideBarSplashLoading={showSplash}
                />
            )}
        </>
    );
}

SideBarV3.propTypes = {
    event: eventShape.isRequired,
    location: routerLocationShape.isRequired,
    sidebarOpen: PropTypes.bool.isRequired,
    toRegConfirmation: PropTypes.func.isRequired,
    handleOverlayClick: PropTypes.func,
    handleRegPartnerAdded: PropTypes.func,
    handleDeclineUpgradeToPowerClick: PropTypes.func,
    clubVerificationFee: PropTypes.number,
    clubId: PropTypes.number,
    setSidebarFlow: PropTypes.func.isRequired,
    multipleDivisionRegistrations: PropTypes.objectOf(PropTypes.shape({
        quantity: PropTypes.number,
        price: PropTypes.number,
    })).isRequired,
    handleMultipleDivisionRegistrations: PropTypes.func.isRequired,
    toMultiRegConfirmation: PropTypes.func.isRequired,
    handleClearMultipleDivisionRegistrations: PropTypes.func.isRequired,
};

SideBarV3.defaultProps = {
    handleOverlayClick: () => { },
    handleRegPartnerAdded: () => { },
    handleDeclineUpgradeToPowerClick: () => { },
    clubVerificationFee: 0,
    clubId: null,
};

export default withRouter(SideBarV3);
